import { Injectable, inject } from '@angular/core';
import { AuthState } from '../../states/auth.state';
import { GetAuthStateResult } from './get-auth-state.result';

/**
 * Get Auth State Use Case
 *
 * Provides read-only access to authentication state signals.
 * Returns only the readable signals and computed values, not the mutation methods.
 *
 * Use this in:
 * - Components that need to read multiple auth state values
 * - Guards that need to check auth status
 *
 * For read-only access to specific values, prefer more specific use cases:
 * - GetAccessTokenUseCase
 * - IsAuthenticatedUseCase
 * - GetUserUseCase
 * - GetUserFullNameUseCase
 *
 * For mutation operations (logout), use dedicated use cases like LogoutUseCase.
 */
@Injectable({ providedIn: 'root' })
export class GetAuthStateUseCase {
  private readonly _authState = inject(AuthState);

  /**
   * Execute the use case
   * @returns Read-only access to auth state signals
   */
  public execute(): GetAuthStateResult {
    return {
      user: this._authState.user,
      accessToken: this._authState.accessToken,
      loading: this._authState.loading,
      error: this._authState.error,
      isAuthenticated: this._authState.isAuthenticated,
      userFullName: this._authState.userFullName,
    };
  }
}
